import { useState, useEffect, useMemo } from 'react';
import { Navigation } from 'lucide-react';
import { Input, Select, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { AddressPicker, buildFullAddress } from '../ui/AddressPicker';
import { MapPicker } from '../ui/MapPicker';
import { getFieldStaff } from '../../api/users';
import { getCustomers } from '../../api/customers';
import { getCurrentLocations } from '../../api/locations';
import { createJob, updateJob } from '../../api/jobs';

function haversine(lat1, lon1, lat2, lon2) {
  const toRad = d => d * Math.PI / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: '🔴 Acil' },
  { value: 'normal', label: '🔵 Normal' },
  { value: 'low',    label: '⚫ Düşük' },
];

export function JobForm({ job, onSuccess, onCancel }) {
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staffLocations, setStaffLocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    title:          job?.title        || '',
    description:    job?.description  || '',
    priority:       job?.priority     || 'normal',
    city:           job?.city         || '',
    district:       job?.district     || '',
    address_detail: job?.address_detail || '',
    latitude:       job?.latitude     || '',
    longitude:      job?.longitude    || '',
    assigned_to:    job ? (job.assigned_to || '') : '__auto__',
    customer_id:    job?.customer_id  || '',
    scheduled_at:   job?.scheduled_at ? job.scheduled_at.slice(0, 16) : '',
    due_at:         job?.due_at       ? job.due_at.slice(0, 16)       : '',
  });

  useEffect(() => {
    getFieldStaff().then(d => setStaff(d.users)).catch(() => {});
    getCustomers({ is_active: true }).then(d => setCustomers(d.customers)).catch(() => {});
    getCurrentLocations().then(d => {
      const map = {};
      d.locations.forEach(loc => { map[loc.user_id] = loc; });
      setStaffLocations(map);
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    setForm(f => {
      const next = { ...f, customer_id: id };
      if (id) {
        const c = customers.find(cu => String(cu.id) === String(id));
        if (c && (c.city || c.district)) {
          next.city           = c.city           || f.city;
          next.district       = c.district       || f.district;
          next.address_detail = c.address_detail || f.address_detail;
          // Müşterinin kayıtlı koordinatları varsa direkt kullan, geocoding yapma
          if (c.latitude && c.longitude) {
            next.latitude  = c.latitude;
            next.longitude = c.longitude;
          } else {
            next.latitude  = '';
            next.longitude = '';
          }
        }
      }
      return next;
    });
  };

  const handleAddressChange = (addrValue) => {
    setForm(f => {
      const cityChanged     = (addrValue.city           ?? f.city)           !== f.city;
      const districtChanged = (addrValue.district       ?? f.district)       !== f.district;
      const detailChanged   = (addrValue.address_detail ?? f.address_detail) !== f.address_detail;
      return {
        ...f,
        city:           addrValue.city           ?? f.city,
        district:       addrValue.district       ?? f.district,
        address_detail: addrValue.address_detail ?? f.address_detail,
        ...(cityChanged || districtChanged || detailChanged ? { latitude: '', longitude: '' } : {}),
      };
    });
  };

  const handleMapChange = ({ latitude, longitude }) => {
    setForm(f => ({ ...f, latitude, longitude }));
    setErrors(e => ({ ...e, address: undefined }));
  };

  const fullAddress = buildFullAddress({
    city: form.city,
    district: form.district,
    address_detail: form.address_detail,
  });

  const geocodeHint = [form.address_detail, form.district, form.city].filter(Boolean).join(', ');

  const jobLat = parseFloat(form.latitude);
  const jobLng = parseFloat(form.longitude);
  const hasJobCoords = form.latitude && form.longitude && isFinite(jobLat) && isFinite(jobLng);

  const staffWithDistance = useMemo(() => {
    return staff.map(s => {
      const loc = staffLocations[s.id];
      if (hasJobCoords && loc) {
        const dist = haversine(jobLat, jobLng, parseFloat(loc.latitude), parseFloat(loc.longitude));
        return { ...s, dist };
      }
      return { ...s, dist: null };
    }).sort((a, b) => {
      if (a.dist === null && b.dist === null) return 0;
      if (a.dist === null) return 1;
      if (b.dist === null) return -1;
      return a.dist - b.dist;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, staffLocations, form.latitude, form.longitude]);

  const nearestStaff = staffWithDistance.find(s => s.dist !== null);

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title   = 'Başlık gerekli';
    if (!form.city)         e.address = 'İl seçin';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const isAutoAssign = form.assigned_to === '__auto__';
      const payload = {
        title:          form.title,
        description:    form.description,
        priority:       form.priority,
        address:        fullAddress,
        city:           form.city           || undefined,
        district:       form.district       || undefined,
        address_detail: form.address_detail || undefined,
        latitude:       form.latitude  ? parseFloat(form.latitude)  : undefined,
        longitude:      form.longitude ? parseFloat(form.longitude) : undefined,
        assigned_to:    (!isAutoAssign && form.assigned_to) ? form.assigned_to : undefined,
        auto_assign:    isAutoAssign,
        customer_id:    form.customer_id  || undefined,
        scheduled_at:   form.scheduled_at || undefined,
        due_at:         form.due_at       || undefined,
      };
      let result;
      if (job) {
        result = await updateJob(job.id, payload);
      } else {
        result = await createJob(payload);
      }

      // Otomatik atama sonucunu göster
      if (result?.auto_assigned) {
        const a = result.auto_assigned;
        const jobNo = result.job?.job_number || '';
        alert(`${jobNo ? jobNo + ' → ' : ''}${a.user_name}'a otomatik atandı (${a.distance} km)`);
      }

      onSuccess();
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Bir hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Başlık *" value={form.title} onChange={set('title')} error={errors.title} placeholder="İş başlığı" />
      <Textarea label="Açıklama" value={form.description} onChange={set('description')} rows={3} placeholder="Detaylar..." />

      <div className="grid grid-cols-2 gap-4">
        <Select label="Öncelik" value={form.priority} onChange={set('priority')}>
          {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
        <Select label="Personel Ata" value={form.assigned_to} onChange={set('assigned_to')}>
          <option value="__auto__">Otomatik Ata (en uygun personel)</option>
          <option value="">— Atanmamış (manuel) —</option>
          {staffWithDistance.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}{s.dist !== null ? ` — ${s.dist.toFixed(1)} km` : ''}
            </option>
          ))}
        </Select>
      </div>
      {nearestStaff && hasJobCoords && form.assigned_to !== '__auto__' && (
        <button
          type="button"
          onClick={() => setForm(f => ({ ...f, assigned_to: nearestStaff.id }))}
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition w-fit -mt-2"
        >
          <Navigation size={12} />
          En yakın: <span className="font-medium">{nearestStaff.name}</span>
          <span className="text-blue-400">— {nearestStaff.dist.toFixed(1)} km · Otomatik Ata</span>
        </button>
      )}

      {/* Müşteri — seçilince adres alanları dolar */}
      <Select label="Müşteri" value={form.customer_id} onChange={handleCustomerChange}>
        <option value="">— Müşteri seçin (isteğe bağlı) —</option>
        {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ''}</option>)}
      </Select>

      {/* İl / İlçe / Açık Adres */}
      <AddressPicker
        value={{ city: form.city, district: form.district, address_detail: form.address_detail }}
        onChange={handleAddressChange}
        error={errors.address}
      />

      {/* Harita */}
      <MapPicker
        geocodeHint={geocodeHint}
        latitude={form.latitude}
        longitude={form.longitude}
        onChange={handleMapChange}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input label="Planlanan Tarih" type="datetime-local" value={form.scheduled_at} onChange={set('scheduled_at')} />
        <Input label="Son Teslim"      type="datetime-local" value={form.due_at}        onChange={set('due_at')} />
      </div>

      {errors.submit && (
        <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg">{errors.submit}</div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>{job ? 'Güncelle' : 'Oluştur'}</Button>
      </div>
    </form>
  );
}
